import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, MenuItem, Select, Typography, type SelectChangeEvent } from '@mui/material'
import { FormLabel } from 'components'
import { color } from 'config/color'
import { useAuth } from 'context'
import { useEffect, useState } from 'react'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'


export interface ServiceAddressType {
    legacyAcctNumber: string
    premiseId: string
    serviceAddress: string
    accountId: string
}

export const ServiceAddress: React.FC = () => {
    const { account, handleCurrentAccount } = useAuth()
    const [serviceAddresses, setServiceAddresses] = useState<ServiceAddressType[]>([])
    const [selectedAddress, setSelectedAddress] = useState<ServiceAddressType>({
        legacyAcctNumber: '',
        premiseId: '',
        serviceAddress: '',
        accountId: ''
    })
    const isMobile = useMobileBreakpoints()
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        if ((account?.myAccount?.serviceAddresses) != null) {
            const uniqueServiceAddresses = account.myAccount?.serviceAddresses.filter(
                (value, index, self) =>
                    self.findIndex((v) => v.accountId === value.accountId && v.premiseId === value.premiseId) === index
            )
            setServiceAddresses(uniqueServiceAddresses)
            setSelectedAddress(uniqueServiceAddresses[0])
        }
    }, [account])


    const getUniqueKey = (address: ServiceAddressType): string => {
        return address.premiseId !== '' ? `${address.premiseId}-${address.accountId}` : address.accountId
    }

    const handleChange = async (e: SelectChangeEvent): Promise<void> => {
        const tempAddress = serviceAddresses.find((address) => getUniqueKey(address) === (e.target.value))
            ; (tempAddress != null) && setSelectedAddress(tempAddress)
        handleCurrentAccount({ accountId: tempAddress?.accountId ?? '', premiseId: tempAddress?.premiseId ?? '' })
    }


    return (
        <> {
            isMobile
                ? <Box display="flex" flexDirection="column" gap={0.5}>
                    <FormLabel
                        id="service-address-mobile-label"
                        htmlFor="service-address-mobile"
                        sx={{
                            color: color.grey[700],
                            fontSize: 12,
                            marginBottom: 0
                        }}
                    >
                        Service Address
                    </FormLabel>
                    <Select
                        labelId="service-address-mobile-label"
                        id="service-address-mobile"
                        variant="standard"
                        value={selectedAddress.accountId !== '' ? getUniqueKey(selectedAddress) : ''}
                        onOpen={() => { setIsOpen(true) }}
                        onClose={() => { setIsOpen(false) }}
                        onChange={handleChange}
                        disableUnderline={true}
                        sx={{
                            fontSize: '16px',
                            '& .MuiSelect-select': {
                                paddingLeft: 0,
                                paddingTop: '4px',
                                paddingBottom: '4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center'
                            }
                        }}
                        IconComponent={() => isOpen ? <ExpandLessIcon sx={{ color: color.primary[500] }} /> : <ExpandMoreIcon sx={{ color: color.primary[500] }} />}
                        inputProps={{
                            id: 'service-address-mobile',
                            'aria-labelledby': 'service-address-mobile-label'
                        }}
                        renderValue={(value) => {
                            const address = serviceAddresses.find(addr => getUniqueKey(addr) === value)
                            return address ? `${address.serviceAddress.toLowerCase().split(',')[0]} | ${address.accountId}` : ''
                        }}
                    >
                        {serviceAddresses.length === 0 && (
                            <MenuItem value="">No active accounts</MenuItem>
                        )}
                        {serviceAddresses.length !== 0 && serviceAddresses.map((address: ServiceAddressType, index) => {
                            return (
                                <MenuItem key={index} value={getUniqueKey(address)}>
                                    <Typography fontSize={14} textTransform="uppercase">
                                        {`${address.serviceAddress.toLowerCase().split(',')[0]} | ${address.accountId}`}
                                    </Typography>
                                </MenuItem>
                            )
                        })}
                    </Select>
                </Box >
                : <Box width="100%">
                    <FormLabel id="service-address-desktop-label" htmlFor="service-address-desktop" sx={{ position: 'absolute', left: '-9999px' }}>Service Address</FormLabel>
                    <Select
                        id="service-address-desktop"
                        labelId="service-address-desktop-label"
                        onOpen={() => { setIsOpen(true) }}
                        onClose={() => { setIsOpen(false) }}
                        fullWidth
                        sx={{
                            fontSize: '14px',
                            color: color.white,
                            border: `solid 1px ${color.primary[800]}`,
                            borderRadius: '0px 0px 2px 2px',
                            width: '100%',
                            '& .MuiSelect-select': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                paddingRight: '32px'
                            }
                        }}
                        value={selectedAddress.accountId !== '' ? getUniqueKey(selectedAddress) : ''}
                        onChange={handleChange}
                        IconComponent={() => isOpen ? <ExpandLessIcon sx={{ color: color.primary[500] }} /> : <ExpandMoreIcon sx={{ color: color.primary[500] }} />}
                        inputProps={{
                            'aria-labelledby': 'service-address-desktop-label'
                        }}
                        renderValue={(value) => {
                            const address = serviceAddresses.find(addr => getUniqueKey(addr) === value)
                            return address ? `${address.serviceAddress} | ${address.accountId}` : ''
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.25)',
                                    borderRadius: 1
                                }
                            }
                        }}
                    >
                        {serviceAddresses.length === 0 && (
                            <MenuItem value="">No active accounts</MenuItem>
                        )}
                        {serviceAddresses.length !== 0 && serviceAddresses.map((address: ServiceAddressType, index) => {
                            return (
                                <MenuItem key={index} value={getUniqueKey(address)}>
                                    <Typography fontSize={14} textTransform="uppercase">
                                        {`${address.serviceAddress} | ${address.accountId}`}
                                    </Typography>
                                </MenuItem>
                            )
                        })}
                    </Select>
                </Box>
        }
        </>
    )
}
